import axios from 'axios';
import { AuthResponse, LoginRequest } from '../types/auth';
import {
  Player,
  CreatePlayerRequest,
  UpdatePlayerRequest,
  UpdatePlayerContactRequest,
  PlayerAbsence,
  SavePlayerAbsenceRequest,
  PlayerImportPreview,
  PlayerImportRow,
  PlayerImportResult,
  PlayerAccountOption,
} from '../types/player';
import type {
  MyTrainingFeedbackState,
  SaveTrainingFeedbackRequest,
  TrainingFeedback,
  TrainingFeedbackSummary,
} from '../types/trainingFeedback';
import type {
  AcceptPlayerApplicationResult,
  PlayerApplication,
  PlayerApplicationStatus,
  SubmitPlayerApplicationRequest,
} from '../types/playerApplication';
import { PLAYER_APPLICATION_STATUS_CODES } from '../types/playerApplication';
import {
  Event,
  CreateEventRequest,
  UpdateEventRequest,
  EventResponse,
  SubmitResponseRequest,
  EventResponseSummary,
  EventComment,
  CreateCommentRequest,
  UpdateCommentRequest,
  ExcuseLetterRequest,
  EventManualReminderResult,
} from '../types/event';
import { Message, Conversation } from '../types/message';
import { Booking, CreateBookingRequest } from '../types/booking';
import {
  BoardPost,
  BoardPostComment,
  BoardPostReader,
  BoardPermissions,
  CreateBoardPostRequest,
  UpdateBoardPostRequest,
} from '../types/board';
import { Venue, CreateVenueRequest, UpdateVenueRequest } from '../types/venue';
import { Document, DocumentListItem, UploadDocumentRequest } from '../types/document';
import {
  Match,
  MatchCreateDto,
  MatchUpdateDto,
  MatchSquad,
  MatchSquadAddDto,
  MatchLineup,
  MatchLineupItemDto,
  MatchSub,
  MatchSubItemDto,
  MatchMinutesPlayed,
  MatchMinutesPlayedItemDto,
  MatchIncident,
  MatchIncidentCreateDto,
  MatchIncidentUpdateDto,
  PlayerMatchStatsDto,
  MatchListItem,
  FormationTemplate,
  ShirtSuggestion,
  MatchSelectionDto,
  MatchResultDto,
  SelectablePlayer,
} from '../types/match';
import {
  Training,
  CreateTrainingRequest,
  CreateRecurringTrainingRequest,
  VenueConflictCheck,
  VenueConflictResult
} from '../types/training';
import {
  AgeGroup,
  AgeGroupSelect,
  AgeGroupPlayer,
  AgeGroupCoach,
  CreateAgeGroupRequest,
  UpdateAgeGroupRequest,
  UpdateAgeGroupCoachesRequest,
  PlayerAgeGroups,
  AddPlayersToAgeGroupResult
} from '../types/ageGroup';
import type {
  MembershipFee,
  MembershipFeeSettings,
  UpdateFeeSettingsRequest,
  UpdatePlayerFeeOverrideRequest,
  MarkFeePaymentRequest,
  FeeStatistics,
  ChildFeeOverview,
  OutstandingFees,
  FeeReminderResult,
  GenerateFeesRequest,
  FeeGenerationResult,
  SendFeeRemindersRequest,
} from '../types/membershipFee';
import type { ClubSettings, UpdateClubSettingsRequest } from '../types/clubSettings';
import type {
  EventPresence,
  EventPresenceInput,
  TrainingPlan,
  PlayerTrainingStats,
} from '../types/trainingSession';
import type { Drill, SaveDrillRequest } from '../types/drill';
import type {
  School,
  SaveSchoolRequest,
  SchoolOption,
  SchoolPlayer,
  MyPlayerSchool,
  SchoolSendPreview,
  SendToSchoolResult,
  BulkSchoolSendResult,
} from '../types/school';
import type {
  Vehicle,
  SaveVehicleRequest,
  EventTravel,
  SaveEventTravelRequest,
  TravelOverview,
  SetPlayerTravelRequest,
  SaveMyRideRequest,
  TravelCopyCandidate,
  CopyTravelResult,
} from '../types/travel';
import type { FamilyAgenda, FamilyResponse } from '../types/family';
import type { TrainingTemplate, SaveTrainingTemplateRequest } from '../types/trainingTemplate';
import type { SearchResponse } from '../types/search';
import type {
  Invitation,
  CreateInvitationRequest,
  CreatedInvitation,
  InvitationPreview,
  BulkInvitationRequest,
  BulkInvitationResult,
} from '../types/invitation';
import type {
  RegistrationRequest,
  SubmitRegistrationRequest,
  ApproveRegistrationRequest,
  RegistrationStatus,
} from '../types/registration';
import type { EmailLogPage, EmailCategory } from '../types/emailLog';
import type {
  VapidPublicKeyResponse,
  PushDevice,
  PushSubscribePayload,
  PushTestResult,
  PushStats,
  AppNotification,
} from '../types/push';
import type {
  OccupancySlot,
  VenueBooking as VenueBookingType,
  CreateVenueBookingRequest,
  UpdateVenueBookingRequest,
  OccupancyConflictCheck,
  OccupancyConflictResult,
} from '../types/venueBooking';
import type {
  AiAnnouncementRequest,
  AiAskResult,
  AiCoachSummaryRequest,
  AiExtractedPlayer,
  AiMatchSummaryRequest,
  AiModelOption,
  AiPlayerEvaluationRequest,
  AiSettings,
  AiStatus,
  AiTestResult,
  AiTextResult,
  AiTrainingPlanRequest,
  AiTrainingPlanResult,
  UpdateAiSettingsRequest,
} from '../types/ai';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5231/api';
export const BACKEND_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A bejelentkezés maga is 401-et ad hibás jelszóra. Ott a kiléptetés
    // értelmetlen: újratöltené az oldalt, és a hibaüzenet elveszne, mielőtt a
    // felhasználó elolvashatná.
    const isLoginAttempt = error.config?.url?.endsWith('/auth/login');

    if (error.response?.status === 401 && !isLoginAttempt) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Fájl letöltése az API-ról (pl. Excel export). A fájlnevet a szerver
 * Content-Disposition fejlécéből vesszük, ha elérhető.
 */
export const downloadFile = async (url: string, fallbackName: string, params?: Record<string, unknown>): Promise<void> => {
  const response = await api.get(url, { params, responseType: 'blob' });
  const disposition: string | undefined = response.headers['content-disposition'];
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const fileName = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = new Blob([response.data], { type: String(response.headers['content-type'] ?? '') });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
};

// Auth API
export const authAPI = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  getCoaches: async (): Promise<any[]> => {
    const response = await api.get('/auth/users/coaches');
    return response.data;
  },

  /** Visszaállító link kérése. A válasz szándékosan ugyanaz, akár létezik a cím, akár nem. */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /** Bejelentkezés nélkül hívható - a token birtoklása a jogosultság. */
  getPasswordReset: async (token: string): Promise<{ email: string }> => {
    const response = await api.get(`/auth/reset-password/${encodeURIComponent(token)}`);
    return response.data;
  },

  /** Új jelszó a levélben kapott linkkel; siker esetén rögtön be is léptet. */
  resetPassword: async (token: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getAll: async (): Promise<any[]> => {
    const response = await api.get('/users');
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (data: any): Promise<any> => {
    const response = await api.post('/users', data);
    return response.data;
  },

  update: async (id: number, data: any): Promise<void> => {
    await api.put(`/users/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  toggleActive: async (id: number): Promise<void> => {
    await api.patch(`/users/${id}/toggle-active`);
  },
};

// Players API
export const playersAPI = {
  /** Több játékos sportorvosi érvényessége egy kérésben (ÉÉÉÉ-HH-NN). */
  setSportDoctorValidity: async (
    playerIds: number[],
    validity: string
  ): Promise<{ updatedCount: number; skippedCount: number }> => {
    const response = await api.put('/players/sport-doctor-validity', { playerIds, validity });
    return response.data;
  },
  getAll: async (includeInactive = false): Promise<Player[]> => {
    const response = await api.get('/players', { params: includeInactive ? { includeInactive: true } : undefined });
    return response.data;
  },

  setActive: async (id: number, active: boolean, reason?: string): Promise<Player> => {
    const response = await api.post(`/players/${id}/active`, { active, reason });
    return response.data;
  },

  getById: async (id: number): Promise<Player> => {
    const response = await api.get(`/players/${id}`);
    return response.data;
  },

  create: async (data: CreatePlayerRequest): Promise<Player> => {
    const response = await api.post('/players', data);
    return response.data;
  },

  update: async (id: number, data: UpdatePlayerRequest): Promise<Player> => {
    const response = await api.put(`/players/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/players/${id}`);
  },

  getByAgeGroup: async (ageGroup: string): Promise<Player[]> => {
    const response = await api.get(`/players/age-group/${ageGroup}`);
    return response.data;
  },

  /** A játékoshoz köthető fiókok a szerkesztő választóihoz (edző is kérheti). */
  getAccountOptions: async (): Promise<PlayerAccountOption[]> => {
    const response = await api.get('/players/account-options');
    return response.data;
  },

  getMyPlayerIds: async (): Promise<number[]> => {
    const response = await api.get('/players/my');
    return response.data;
  },

  /** A saját játékos és a gyermekek teljes adatlappal. */
  getMyPlayers: async (): Promise<Player[]> => {
    const response = await api.get('/players/my/details');
    return response.data;
  },

  /** Elérhetőségek frissítése - a szülő és a játékos maga is hívhatja. */
  updateContact: async (id: number, data: UpdatePlayerContactRequest): Promise<Player> => {
    const response = await api.put(`/players/${id}/contact`, data);
    return response.data;
  },

  downloadImportTemplate: async (): Promise<void> => {
    await downloadFile('/players/import/template.xlsx', 'jatekos-import-minta.xlsx');
  },

  previewImport: async (file: File): Promise<PlayerImportPreview> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/players/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  import: async (rows: PlayerImportRow[]): Promise<PlayerImportResult> => {
    const response = await api.post('/players/import', { rows });
    return response.data;
  },
};

/** Betegség / sérülés bejelentések. */
export const absencesAPI = {
  getCurrent: async (): Promise<PlayerAbsence[]> => {
    const response = await api.get('/absences/current');
    return response.data;
  },
  getForPlayer: async (playerId: number): Promise<PlayerAbsence[]> => {
    const response = await api.get(`/players/${playerId}/absences`);
    return response.data;
  },
  create: async (playerId: number, data: SavePlayerAbsenceRequest): Promise<PlayerAbsence> => {
    const response = await api.post(`/players/${playerId}/absences`, data);
    return response.data;
  },
  update: async (id: number, data: SavePlayerAbsenceRequest): Promise<PlayerAbsence> => {
    const response = await api.put(`/absences/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/absences/${id}`);
  },
};

export interface CalendarFeedLink {
  url: string;
  webcalUrl: string;
}

/** Személyes naptár-előfizetés (iCal). */
export const calendarFeedAPI = {
  getLink: async (): Promise<CalendarFeedLink> => {
    const response = await api.get('/calendar/feed-link');
    return response.data;
  },
  reset: async (): Promise<CalendarFeedLink> => {
    const response = await api.post('/calendar/feed-link/reset');
    return response.data;
  },
};

/** Excel exportok. */
export const exportsAPI = {
  players: (includeInactive: boolean) =>
    downloadFile('/exports/players.xlsx', 'jatekosok.xlsx', includeInactive ? { includeInactive: true } : undefined),
  attendance: (params: { ageGroupId?: number; dateFrom?: string; dateTo?: string }) =>
    downloadFile('/exports/attendance.xlsx', 'jelenlet.xlsx', params),
  fees: (year: number) => downloadFile('/exports/fees.xlsx', `tagdijak-${year}.xlsx`, { year }),
};

export const playerApplicationsAPI = {
  /** Bejelentkezés nélkül hívható - a nyilvános jelentkezési űrlap. */
  submit: async (data: SubmitPlayerApplicationRequest): Promise<void> => {
    await api.post('/player-applications', data);
  },
  getAll: async (status?: PlayerApplicationStatus): Promise<PlayerApplication[]> => {
    const response = await api.get('/player-applications', {
      params: status ? { status: PLAYER_APPLICATION_STATUS_CODES[status] } : undefined,
    });
    return response.data;
  },
  getPendingCount: async (): Promise<number> => {
    const response = await api.get('/player-applications/pending-count');
    return response.data;
  },
  accept: async (id: number, ageGroupIds: number[], inviteParent: boolean): Promise<AcceptPlayerApplicationResult> => {
    const response = await api.post(`/player-applications/${id}/accept`, { ageGroupIds, inviteParent });
    return response.data;
  },
  reject: async (id: number, reason: string | undefined, notifyParent: boolean): Promise<PlayerApplication> => {
    const response = await api.post(`/player-applications/${id}/reject`, { reason, notifyParent });
    return response.data;
  },
};

// Events API
export const eventsAPI = {
  getAll: async (): Promise<Event[]> => {
    const response = await api.get('/events');
    return response.data;
  },

  getById: async (id: number): Promise<Event> => {
    const response = await api.get(`/events/${id}`);
    return response.data;
  },

  create: async (data: CreateEventRequest): Promise<Event> => {
    const response = await api.post('/events', data);
    return response.data;
  },

  update: async (id: number, data: UpdateEventRequest): Promise<Event> => {
    const response = await api.put(`/events/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/events/${id}`);
  },

  getByDateRange: async (startDate: string, endDate: string): Promise<Event[]> => {
    const response = await api.get(`/events/date-range?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  },

  getByAgeGroup: async (ageGroup: string): Promise<Event[]> => {
    const response = await api.get(`/events/age-group/${ageGroup}`);
    return response.data;
  },

  getUpcoming: async (days: number = 7): Promise<Event[]> => {
    const response = await api.get(`/events/upcoming?days=${days}`);
    return response.data;
  },

  getAllForConflictCheck: async (): Promise<Event[]> => {
    const response = await api.get('/events/all-for-conflict-check');
    return response.data;
  },

  // Response/Attendance endpoints
  submitResponse: async (eventId: number, data: SubmitResponseRequest): Promise<EventResponse> => {
    const response = await api.post(`/events/${eventId}/response`, data);
    return response.data;
  },

  getResponses: async (eventId: number): Promise<EventResponse[]> => {
    const response = await api.get(`/events/${eventId}/responses`);
    return response.data;
  },

  getResponseSummary: async (eventId: number): Promise<EventResponseSummary> => {
    const response = await api.get(`/events/${eventId}/responses/summary`);
    return response.data;
  },

  // Comment endpoints
  getComments: async (eventId: number): Promise<EventComment[]> => {
    const response = await api.get(`/events/${eventId}/comments`);
    return response.data;
  },

  createComment: async (eventId: number, data: CreateCommentRequest): Promise<EventComment> => {
    const response = await api.post(`/events/${eventId}/comments`, data);
    return response.data;
  },

  updateComment: async (eventId: number, commentId: number, data: UpdateCommentRequest): Promise<void> => {
    await api.put(`/events/${eventId}/comments/${commentId}`, data);
  },

  deleteComment: async (eventId: number, commentId: number): Promise<void> => {
    await api.delete(`/events/${eventId}/comments/${commentId}`);
  },

  // Excuse Letter endpoints
  requestExcuseLetter: async (eventId: number, playerId: number): Promise<Document> => {
    const response = await api.post(`/events/${eventId}/excuse-letter`, { playerId });
    return response.data;
  },

  hasExcuseLetterRequested: async (eventId: number, playerId: number): Promise<boolean> => {
    const response = await api.get(`/events/${eventId}/excuse-letter/${playerId}`);
    return response.data;
  },

  sendReminder: async (eventId: number, onlyNonResponders: boolean): Promise<EventManualReminderResult> => {
    const response = await api.post(`/events/${eventId}/remind`, { onlyNonResponders });
    return response.data;
  },

  getExcuseLetterRequests: async (eventId: number): Promise<ExcuseLetterRequest[]> => {
    const response = await api.get(`/events/${eventId}/excuse-letter/requests`);
    return response.data;
  },

  generateExcuseLetters: async (eventId: number): Promise<Document[]> => {
    const response = await api.post(`/events/${eventId}/excuse-letter/generate`);
    return response.data;
  },

  /** A saját játékos / gyermekek visszajelzése az edzésről. */
  getMyFeedback: async (eventId: number): Promise<MyTrainingFeedbackState> => {
    const response = await api.get(`/events/${eventId}/feedback/mine`);
    return response.data;
  },

  saveFeedback: async (eventId: number, data: SaveTrainingFeedbackRequest): Promise<TrainingFeedback> => {
    const response = await api.put(`/events/${eventId}/feedback`, data);
    return response.data;
  },

  /** Az edző nézete: átlagok és megjegyzések. */
  getFeedbackSummary: async (eventId: number): Promise<TrainingFeedbackSummary> => {
    const response = await api.get(`/events/${eventId}/feedback`);
    return response.data;
  },

  getMatch: async (eventId: number): Promise<Match> => {
    const response = await api.get(`/events/${eventId}/match`);
    return response.data;
  },

  createMatch: async (eventId: number, data: MatchCreateDto): Promise<Match> => {
    const response = await api.post(`/events/${eventId}/match`, data);
    return response.data;
  },
};

// Matches API (by matchId)
export const matchesAPI = {
  getAll: async (params?: { ageGroupId?: number; dateFrom?: string; dateTo?: string }): Promise<MatchListItem[]> => {
    const searchParams = new URLSearchParams();
    if (params?.ageGroupId != null) searchParams.set('ageGroupId', String(params.ageGroupId));
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    const response = await api.get(`/matches?${searchParams.toString()}`);
    return response.data;
  },

  update: async (matchId: number, data: MatchUpdateDto): Promise<Match> => {
    const response = await api.put(`/matches/${matchId}`, data);
    return response.data;
  },

  getSelectablePlayers: async (): Promise<SelectablePlayer[]> => {
    const response = await api.get('/matches/selectable-players');
    return response.data;
  },

  getShirtSuggestions: async (matchId: number): Promise<ShirtSuggestion[]> => {
    const response = await api.get(`/matches/${matchId}/shirt-suggestions`);
    return response.data;
  },

  setSelection: async (matchId: number, data: MatchSelectionDto): Promise<Match> => {
    const response = await api.put(`/matches/${matchId}/selection`, data);
    return response.data;
  },

  setResult: async (matchId: number, data: MatchResultDto): Promise<Match> => {
    const response = await api.put(`/matches/${matchId}/result`, data);
    return response.data;
  },

  getSquad: async (matchId: number): Promise<MatchSquad[]> => {
    const response = await api.get(`/matches/${matchId}/squad`);
    return response.data;
  },

  addToSquad: async (matchId: number, data: MatchSquadAddDto): Promise<MatchSquad> => {
    const response = await api.post(`/matches/${matchId}/squad`, data);
    return response.data;
  },

  removeFromSquad: async (matchId: number, playerId: number): Promise<void> => {
    await api.delete(`/matches/${matchId}/squad/${playerId}`);
  },

  getLineup: async (matchId: number): Promise<MatchLineup[]> => {
    const response = await api.get(`/matches/${matchId}/lineup`);
    return response.data;
  },

  setLineup: async (matchId: number, items: MatchLineupItemDto[]): Promise<void> => {
    await api.put(`/matches/${matchId}/lineup`, items);
  },

  getSubs: async (matchId: number): Promise<MatchSub[]> => {
    const response = await api.get(`/matches/${matchId}/subs`);
    return response.data;
  },

  setSubs: async (matchId: number, items: MatchSubItemDto[]): Promise<void> => {
    await api.put(`/matches/${matchId}/subs`, items);
  },

  getMinutesPlayed: async (matchId: number): Promise<MatchMinutesPlayed[]> => {
    const response = await api.get(`/matches/${matchId}/minutes`);
    return response.data;
  },

  setMinutesPlayed: async (matchId: number, items: MatchMinutesPlayedItemDto[]): Promise<void> => {
    await api.put(`/matches/${matchId}/minutes`, items);
  },

  getIncidents: async (matchId: number): Promise<MatchIncident[]> => {
    const response = await api.get(`/matches/${matchId}/incidents`);
    return response.data;
  },

  addIncident: async (matchId: number, data: MatchIncidentCreateDto): Promise<MatchIncident> => {
    const response = await api.post(`/matches/${matchId}/incidents`, data);
    return response.data;
  },

  updateIncident: async (matchId: number, incidentId: number, data: MatchIncidentUpdateDto): Promise<MatchIncident> => {
    const response = await api.put(`/matches/${matchId}/incidents/${incidentId}`, data);
    return response.data;
  },

  deleteIncident: async (matchId: number, incidentId: number): Promise<void> => {
    await api.delete(`/matches/${matchId}/incidents/${incidentId}`);
  },

  getStatistics: async (params?: { playerId?: number; ageGroupId?: number; dateFrom?: string; dateTo?: string }): Promise<PlayerMatchStatsDto[]> => {
    const searchParams = new URLSearchParams();
    if (params?.playerId != null) searchParams.set('playerId', String(params.playerId));
    if (params?.ageGroupId != null) searchParams.set('ageGroupId', String(params.ageGroupId));
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    const response = await api.get(`/matches/statistics?${searchParams.toString()}`);
    return response.data;
  },
};

// Messages API
export const messagesAPI = {
  getConversations: async (): Promise<Conversation[]> => {
    const response = await api.get('/messages/conversations');
    return response.data;
  },

  getConversation: async (otherUserId: number, skip: number = 0, take: number = 50): Promise<Message[]> => {
    const response = await api.get(`/messages/conversation/${otherUserId}?skip=${skip}&take=${take}`);
    return response.data;
  },

  getChannelMessages: async (channelId: number, skip: number = 0, take: number = 50): Promise<Message[]> => {
    const response = await api.get(`/messages/channel/${channelId}?skip=${skip}&take=${take}`);
    return response.data;
  },

  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/messages/unread-count');
    return response.data;
  },

  markAsRead: async (messageId: number): Promise<void> => {
    await api.post(`/messages/${messageId}/mark-read`);
  },

  sendMessage: async (receiverUserId: number, content: string): Promise<Message> => {
    const response = await api.post('/messages/send', {
      content,
      receiverUserId,
      type: 1, // MessageType.Text
    });
    return response.data;
  },
};

// Venue API
export const venuesAPI = {
  getAllVenues: async (): Promise<Venue[]> => {
    const response = await api.get('/venues');
    return response.data;
  },

  getVenue: async (id: number): Promise<Venue> => {
    const response = await api.get(`/venues/${id}`);
    return response.data;
  },

  createVenue: async (data: CreateVenueRequest): Promise<Venue> => {
    const response = await api.post('/venues', data);
    return response.data;
  },

  updateVenue: async (id: number, data: UpdateVenueRequest): Promise<Venue> => {
    const response = await api.put(`/venues/${id}`, data);
    return response.data;
  },

  deleteVenue: async (id: number): Promise<void> => {
    await api.delete(`/venues/${id}`);
  },
};

// Documents API
export const documentsAPI = {
  getPlayerDocumentList: async (searchTerm?: string): Promise<DocumentListItem[]> => {
    const response = await api.get('/documents/players', {
      params: { searchTerm },
    });
    return response.data;
  },

  getUserDocumentList: async (searchTerm?: string): Promise<DocumentListItem[]> => {
    const response = await api.get('/documents/users', {
      params: { searchTerm },
    });
    return response.data;
  },

  getDocumentsByPlayerId: async (playerId: number): Promise<Document[]> => {
    const response = await api.get(`/documents/player/${playerId}`);
    return response.data;
  },

  getDocumentsByUserId: async (userId: number): Promise<Document[]> => {
    const response = await api.get(`/documents/user/${userId}`);
    return response.data;
  },

  getPublicDocuments: async (): Promise<Document[]> => {
    const response = await api.get('/documents/public');
    return response.data;
  },

  uploadDocument: async (file: File, request: UploadDocumentRequest): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', request.type.toString());
    if (request.description) formData.append('description', request.description);
    if (request.playerId) formData.append('playerId', request.playerId.toString());
    if (request.userId) formData.append('userId', request.userId.toString());
    formData.append('isPublic', request.isPublic.toString());

    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadDocument: async (id: number, fileName: string): Promise<void> => {
    const response = await api.get(`/documents/download/${id}`, {
      responseType: 'blob',
    });

    // Create a blob URL and trigger download
    const blob = new Blob([response.data], { type: String(response.headers['content-type'] ?? '') });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  deleteDocument: async (id: number): Promise<void> => {
    await api.delete(`/documents/${id}`);
  },
};

// Trainings API
// A stáb közös edzéssablonjai (teljes edzéstervek)
export const trainingTemplatesAPI = {
  getAll: async (params: { search?: string; onlyMine?: boolean } = {}): Promise<TrainingTemplate[]> => {
    const response = await api.get('/training-templates', { params });
    return response.data;
  },

  create: async (data: SaveTrainingTemplateRequest): Promise<TrainingTemplate> => {
    const response = await api.post('/training-templates', data);
    return response.data;
  },

  update: async (id: number, data: SaveTrainingTemplateRequest): Promise<TrainingTemplate> => {
    const response = await api.put(`/training-templates/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/training-templates/${id}`);
  },
};

// A stáb közös gyakorlattára
export const formationTemplatesAPI = {
  getAll: async (): Promise<FormationTemplate[]> => {
    const response = await api.get('/formation-templates');
    return response.data;
  },

  create: async (data: { name: string; slots: { x: number; y: number; role: string }[] }): Promise<FormationTemplate> => {
    const response = await api.post('/formation-templates', data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/formation-templates/${id}`);
  },
};

export const drillsAPI = {
  getAll: async (params: { search?: string; category?: string; onlyMine?: boolean } = {}): Promise<Drill[]> => {
    const response = await api.get('/drills', { params });
    return response.data;
  },

  get: async (id: number): Promise<Drill> => {
    const response = await api.get(`/drills/${id}`);
    return response.data;
  },

  create: async (data: SaveDrillRequest): Promise<Drill> => {
    const response = await api.post('/drills', data);
    return response.data;
  },

  update: async (id: number, data: SaveDrillRequest): Promise<Drill> => {
    const response = await api.put(`/drills/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/drills/${id}`);
  },
};

// Edzés lebonyolítása: jelenlét, edzésterv, látogatottsági statisztika
export const schoolsAPI = {
  getAll: async (): Promise<School[]> => {
    const response = await api.get('/schools');
    return response.data;
  },

  getOptions: async (): Promise<SchoolOption[]> => {
    const response = await api.get('/schools/options');
    return response.data;
  },

  create: async (data: SaveSchoolRequest): Promise<School> => {
    const response = await api.post('/schools', data);
    return response.data;
  },

  update: async (id: number, data: SaveSchoolRequest): Promise<School> => {
    const response = await api.put(`/schools/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/schools/${id}`);
  },

  getPlayers: async (id: number): Promise<SchoolPlayer[]> => {
    const response = await api.get(`/schools/${id}/players`);
    return response.data;
  },

  getMyPlayers: async (): Promise<MyPlayerSchool[]> => {
    const response = await api.get('/schools/my-players');
    return response.data;
  },

  setMyPlayerSchool: async (playerId: number, schoolId: number | null): Promise<void> => {
    await api.put(`/schools/my-players/${playerId}`, { schoolId });
  },

  getSendPreview: async (documentId: number): Promise<SchoolSendPreview> => {
    const response = await api.get(`/documents/${documentId}/school-send`);
    return response.data;
  },

  sendDocument: async (
    documentId: number,
    data: { message?: string | null; copyFamily: boolean }
  ): Promise<SendToSchoolResult> => {
    const response = await api.post(`/documents/${documentId}/send-to-school`, data);
    return response.data;
  },

  sendEventExcuseLetters: async (eventId: number): Promise<BulkSchoolSendResult> => {
    const response = await api.post(`/events/${eventId}/excuse-letter/send-to-schools`);
    return response.data;
  },
};

export const travelAPI = {
  getVehicles: async (includeInactive = false): Promise<Vehicle[]> => {
    const response = await api.get('/vehicles', { params: { includeInactive } });
    return response.data;
  },

  createVehicle: async (data: SaveVehicleRequest): Promise<Vehicle> => {
    const response = await api.post('/vehicles', data);
    return response.data;
  },

  updateVehicle: async (id: number, data: SaveVehicleRequest): Promise<Vehicle> => {
    const response = await api.put(`/vehicles/${id}`, data);
    return response.data;
  },

  deleteVehicle: async (id: number): Promise<void> => {
    await api.delete(`/vehicles/${id}`);
  },

  getUpcoming: async (days = 60): Promise<TravelOverview[]> => {
    const response = await api.get('/travel/upcoming', { params: { days } });
    return response.data;
  },

  getTravel: async (eventId: number): Promise<EventTravel> => {
    const response = await api.get(`/events/${eventId}/travel`);
    return response.data;
  },

  saveTravel: async (eventId: number, data: SaveEventTravelRequest): Promise<EventTravel> => {
    const response = await api.put(`/events/${eventId}/travel`, data);
    return response.data;
  },

  deleteTravel: async (eventId: number): Promise<void> => {
    await api.delete(`/events/${eventId}/travel`);
  },

  setPlayerTravel: async (eventId: number, playerId: number, data: SetPlayerTravelRequest): Promise<EventTravel> => {
    const response = await api.put(`/events/${eventId}/travel/players/${playerId}`, data);
    return response.data;
  },

  saveMyRide: async (eventId: number, data: SaveMyRideRequest): Promise<EventTravel> => {
    const response = await api.put(`/events/${eventId}/travel/my-ride`, data);
    return response.data;
  },

  deleteMyRide: async (eventId: number): Promise<EventTravel> => {
    const response = await api.delete(`/events/${eventId}/travel/my-ride`);
    return response.data;
  },

  getCopyTargets: async (eventId: number): Promise<TravelCopyCandidate[]> => {
    const response = await api.get(`/events/${eventId}/travel/copy-targets`);
    return response.data;
  },

  getCopySources: async (eventId: number): Promise<TravelCopyCandidate[]> => {
    const response = await api.get(`/events/${eventId}/travel/copy-sources`);
    return response.data;
  },

  copyTravel: async (eventId: number, targetEventIds: number[], includePassengers: boolean): Promise<CopyTravelResult> => {
    const response = await api.post(`/events/${eventId}/travel/copy`, { targetEventIds, includePassengers });
    return response.data;
  },
};

export const familyAPI = {
  /** A saját játékosok és a program; a dátumok ÉÉÉÉ-HH-NN alakban. */
  getAgenda: async (from?: string, to?: string): Promise<FamilyAgenda> => {
    const response = await api.get('/family/agenda', { params: { from, to } });
    return response.data;
  },

  /** A saját játékosok egy eseménynél: jön-e, benne van-e a keretben, kért-e kikérőt. */
  getEventPlayers: async (eventId: number): Promise<FamilyResponse[]> => {
    const response = await api.get(`/family/events/${eventId}/players`);
    return response.data;
  },
};

export const trainingSessionAPI = {
  getPresence: async (eventId: number): Promise<EventPresence> => {
    const response = await api.get(`/events/${eventId}/presence`);
    return response.data;
  },

  savePresence: async (eventId: number, items: EventPresenceInput[]): Promise<EventPresence> => {
    const response = await api.put(`/events/${eventId}/presence`, { items });
    return response.data;
  },

  getPlan: async (eventId: number): Promise<TrainingPlan> => {
    const response = await api.get(`/events/${eventId}/training-plan`);
    return response.data;
  },

  savePlan: async (eventId: number, plan: Omit<TrainingPlan, 'eventId'>): Promise<TrainingPlan> => {
    const response = await api.put(`/events/${eventId}/training-plan`, { eventId, ...plan });
    return response.data;
  },

  getStats: async (params: { ageGroupId?: number; dateFrom?: string; dateTo?: string } = {}): Promise<PlayerTrainingStats[]> => {
    const response = await api.get('/trainings/attendance-stats', { params });
    return response.data;
  },
};

export const trainingsAPI = {
  getAll: async (): Promise<Training[]> => {
    const response = await api.get('/trainings');
    return response.data;
  },

  getById: async (id: number): Promise<Training> => {
    const response = await api.get(`/trainings/${id}`);
    return response.data;
  },

  create: async (data: CreateTrainingRequest): Promise<Training> => {
    const response = await api.post('/trainings', data);
    return response.data;
  },

  createRecurring: async (data: CreateRecurringTrainingRequest): Promise<Training[]> => {
    const response = await api.post('/trainings/recurring', data);
    return response.data;
  },

  checkVenueConflict: async (data: VenueConflictCheck): Promise<VenueConflictResult> => {
    const response = await api.post('/trainings/check-venue-conflict', data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/trainings/${id}`);
  },

  cancel: async (id: number): Promise<{ message: string }> => {
    const response = await api.post(`/trainings/${id}/cancel`);
    return response.data;
  },
};

// AgeGroups API
export const ageGroupsAPI = {
  getAll: async (): Promise<AgeGroup[]> => {
    const response = await api.get('/agegroups');
    return response.data;
  },

  getForSelect: async (): Promise<AgeGroupSelect[]> => {
    const response = await api.get('/agegroups/select');
    return response.data;
  },

  getById: async (id: number): Promise<AgeGroup> => {
    const response = await api.get(`/agegroups/${id}`);
    return response.data;
  },

  create: async (data: CreateAgeGroupRequest): Promise<AgeGroup> => {
    const response = await api.post('/agegroups', data);
    return response.data;
  },

  update: async (id: number, data: UpdateAgeGroupRequest): Promise<AgeGroup> => {
    const response = await api.put(`/agegroups/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/agegroups/${id}`);
  },

  getPlayers: async (id: number): Promise<AgeGroupPlayer[]> => {
    const response = await api.get(`/agegroups/${id}/players`);
    return response.data;
  },

  getSuggestedPlayers: async (id: number): Promise<AgeGroupPlayer[]> => {
    const response = await api.get(`/agegroups/${id}/suggested-players`);
    return response.data;
  },

  addPlayer: async (ageGroupId: number, playerId: number, removeFromAgeGroupIds?: number[]): Promise<void> => {
    const body = removeFromAgeGroupIds && removeFromAgeGroupIds.length > 0
      ? { playerId, removeFromAgeGroupIds }
      : undefined;
    await api.post(`/agegroups/${ageGroupId}/players/${playerId}`, body);
  },

  addPlayers: async (
    ageGroupId: number,
    playerIds: number[],
    removeFromOtherAgeGroups: boolean,
  ): Promise<AddPlayersToAgeGroupResult> => {
    const response = await api.post(`/agegroups/${ageGroupId}/players/bulk`, {
      playerIds,
      removeFromOtherAgeGroups,
    });
    return response.data;
  },

  removePlayer: async (ageGroupId: number, playerId: number): Promise<void> => {
    await api.delete(`/agegroups/${ageGroupId}/players/${playerId}`);
  },

  getPlayerAgeGroups: async (playerId: number): Promise<PlayerAgeGroups> => {
    const response = await api.get(`/agegroups/player/${playerId}/agegroups`);
    return response.data;
  },

  getCoaches: async (id: number): Promise<AgeGroupCoach[]> => {
    const response = await api.get(`/agegroups/${id}/coaches`);
    return response.data;
  },

  updateCoaches: async (id: number, data: UpdateAgeGroupCoachesRequest): Promise<void> => {
    await api.put(`/agegroups/${id}/coaches`, data);
  },

  suggestForBirthDate: async (birthDate: string): Promise<AgeGroupSelect | null> => {
    const response = await api.get(`/agegroups/suggest?birthDate=${birthDate}`);
    return response.data;
  },
};

// MLSZ IFA API
export interface MlszStepResult {
  step: number;
  description: string;
  success: boolean;
  error?: string;
}

export interface MlszPlayerData {
  id: string;
  nev: string;
  nem: string;
  szulDatum: string;
  szulHely: string;
  allampolgarsag: string;
  szuletesiOrszag: string;
  anyjaNeve: string;
  tajSzam: string;
  regKod: string;
  sportorvosiErvenyesseg: string;
}

/** Egy egyesulet az IFA egyesuletvalaszto legorduljebol. */
export interface MlszClub {
  id: string;
  name: string;
}

export interface MlszLoginResponse {
  success: boolean;
  steps: MlszStepResult[];
  players: MlszPlayerData[];
  existingIfaCodes: string[];
  pageContent?: string;
  error?: string;
  /** A fiokhoz tartozo egyesuletek. */
  clubs: MlszClub[];
  /** Melyik egyesulet adatait tartalmazza a valasz. */
  selectedClubId?: string | null;
  /** True, ha tobb egyesulet kozul kell valasztani (a jatekoslista ilyenkor ures). */
  clubSelectionRequired: boolean;
  /** True, ha a rendszerhez kötött egyesület nem tartozik ehhez az IFA fiókhoz. */
  clubMismatch?: boolean;
  /** Diagnosztika: az oldalon talalt legordulok, ha az egyesuletvalaszto nem volt azonosithato. */
  availableSelects: string[];
}

export interface MlszImportResult {
  importedCount: number;
  skippedCount: number;
  importedNames: string[];
  skippedNames: string[];
  error?: string;
}

export interface MlszSquadPlayer {
  regKod: string;
  name: string;
  birth: string;
  sportDoctorValidity: string;
  /** Ha ez az IFA játékos már össze van kötve nálunk valakivel. */
  linkedPlayerId?: number | null;
  linkedPlayerName?: string | null;
}

export interface MlszSquadResult {
  success: boolean;
  error?: string | null;
  players: MlszSquadPlayer[];
}

export interface MlszLinkFieldChange {
  field: string;
  oldValue?: string | null;
  newValue?: string | null;
}

export interface MlszLinkResult {
  success: boolean;
  error?: string | null;
  relinkConfirmationRequired: boolean;
  previousRegKod?: string | null;
  changes: MlszLinkFieldChange[];
}

export const mlszAPI = {
  /** A klub IFA-kerete az összekötés választólistájához. */
  getSquad: async (): Promise<MlszSquadResult> => {
    const response = await api.get('/mlsz/squad');
    return response.data;
  },

  /** Helyi játékos összekötése egy IFA-belivel. */
  linkPlayer: async (playerId: number, regKod: string, confirmRelink = false): Promise<MlszLinkResult> => {
    const response = await api.post('/mlsz/link', { playerId, regKod, confirmRelink });
    return response.data;
  },

  /** A kijelölt játékosok sportorvosi dátumának frissítése az IFA-ból. */
  syncSportDoctor: async (playerIds: number[]): Promise<SportDoctorSyncResult> => {
    const response = await api.post('/mlsz/sync-sport-doctor', { playerIds });
    return response.data;
  },

  login: async (email: string, password: string, clubId?: string | null): Promise<MlszLoginResponse> => {
    const response = await api.post('/mlsz/login', { email, password, clubId: clubId || undefined });
    return response.data;
  },
  importPlayers: async (
    email: string,
    password: string,
    players: MlszPlayerData[],
    clubId?: string | null
  ): Promise<MlszImportResult> => {
    const response = await api.post('/mlsz/import', {
      email,
      password,
      players,
      clubId: clubId || undefined,
    });
    return response.data;
  },
};

export const membershipFeesAPI = {
  getSettings: async (): Promise<MembershipFeeSettings> => {
    const response = await api.get('/membershipfees/settings');
    return response.data;
  },
  updateSettings: async (data: UpdateFeeSettingsRequest): Promise<MembershipFeeSettings> => {
    const response = await api.put('/membershipfees/settings', data);
    return response.data;
  },
  getFees: async (year: number, month: number): Promise<MembershipFee[]> => {
    const response = await api.get(`/membershipfees?year=${year}&month=${month}`);
    return response.data;
  },
  getFeesByPlayer: async (playerId: number): Promise<MembershipFee[]> => {
    const response = await api.get(`/membershipfees/player/${playerId}`);
    return response.data;
  },
  setPlayerOverride: async (playerId: number, data: UpdatePlayerFeeOverrideRequest): Promise<void> => {
    await api.put(`/membershipfees/player/${playerId}/override`, data);
  },
  /** Több tagdíj jelölése egy kérésben. */
  markPayments: async (feeIds: number[], isPaid: boolean): Promise<{ updatedCount: number }> => {
    const response = await api.put('/membershipfees/payments', { feeIds, isPaid });
    return response.data;
  },
  markPayment: async (id: number, data: MarkFeePaymentRequest): Promise<void> => {
    await api.put(`/membershipfees/${id}/payment`, data);
  },
  updateFeeAmount: async (id: number, amount: number): Promise<void> => {
    await api.put(`/membershipfees/${id}/amount`, { amount });
  },
  generate: async (year: number, month: number): Promise<{ count: number }> => {
    const response = await api.post(`/membershipfees/generate?year=${year}&month=${month}`);
    return response.data;
  },
  getChildrenFees: async (year?: number): Promise<ChildFeeOverview[]> => {
    const params = year ? `?year=${year}` : '';
    const response = await api.get(`/membershipfees/my-children${params}`);
    return response.data;
  },
  getStatistics: async (year: number, month: number): Promise<FeeStatistics> => {
    const response = await api.get(`/membershipfees/statistics?year=${year}&month=${month}`);
    return response.data;
  },
  getOutstanding: async (): Promise<OutstandingFees> => {
    const response = await api.get('/membershipfees/outstanding');
    return response.data;
  },
  /** Emlékeztető a tartozóknak most. Üres lista = minden látott tartozó. */
  sendReminders: async (request: SendFeeRemindersRequest): Promise<FeeReminderResult> => {
    const response = await api.post('/membershipfees/reminders', request);
    return response.data;
  },
  previewGeneration: async (request: GenerateFeesRequest): Promise<FeeGenerationResult> => {
    const response = await api.post('/membershipfees/generate/preview', request);
    return response.data;
  },
  runGeneration: async (request: GenerateFeesRequest): Promise<FeeGenerationResult> => {
    const response = await api.post('/membershipfees/generate/run', request);
    return response.data;
  },
};

export const clubSettingsAPI = {
  getSettings: async (): Promise<ClubSettings> => {
    const response = await api.get('/club-settings');
    return response.data;
  },
  updateSettings: async (data: UpdateClubSettingsRequest): Promise<ClubSettings> => {
    const response = await api.put('/club-settings', data);
    return response.data;
  },
  uploadCrest: async (file: File): Promise<ClubSettings> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/club-settings/crest', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteCrest: async (): Promise<ClubSettings> => {
    const response = await api.delete('/club-settings/crest');
    return response.data;
  },
  clearMlszClub: async (): Promise<ClubSettings> => {
    const response = await api.delete('/club-settings/mlsz-club');
    return response.data;
  },
};

export const venueBookingsAPI = {
  getWeeklyOccupancy: async (venueId: number, weekStart: string): Promise<OccupancySlot[]> => {
    const response = await api.get(`/venue-bookings/occupancy/${venueId}/weekly?weekStart=${weekStart}`);
    return response.data;
  },
  getDailyOccupancy: async (venueId: number, date: string): Promise<OccupancySlot[]> => {
    const response = await api.get(`/venue-bookings/occupancy/${venueId}/daily?date=${date}`);
    return response.data;
  },
  createBooking: async (data: CreateVenueBookingRequest): Promise<VenueBookingType> => {
    const response = await api.post('/venue-bookings', data);
    return response.data;
  },
  updateBooking: async (id: number, data: UpdateVenueBookingRequest): Promise<VenueBookingType> => {
    const response = await api.put(`/venue-bookings/${id}`, data);
    return response.data;
  },
  deleteBooking: async (id: number): Promise<void> => {
    await api.delete(`/venue-bookings/${id}`);
  },
  checkConflict: async (data: OccupancyConflictCheck): Promise<OccupancyConflictResult> => {
    const response = await api.post('/venue-bookings/check-conflict', data);
    return response.data;
  },
};

export const pushAPI = {
  getVapidPublicKey: async (): Promise<VapidPublicKeyResponse> => {
    const response = await api.get('/push/vapid-public-key');
    return response.data;
  },
  subscribe: async (data: PushSubscribePayload): Promise<PushDevice> => {
    const response = await api.post('/push/subscribe', data);
    return response.data;
  },
  unsubscribe: async (endpoint: string): Promise<void> => {
    await api.post('/push/unsubscribe', { endpoint });
  },
  getDevices: async (): Promise<PushDevice[]> => {
    const response = await api.get('/push/devices');
    return response.data;
  },
  deleteDevice: async (id: number): Promise<void> => {
    await api.delete(`/push/devices/${id}`);
  },
  /** Teszt értesítés a saját eszközökre. Hibánál is 200-at ad, a részletekkel. */
  sendTest: async (): Promise<PushTestResult> => {
    const response = await api.post('/push/test');
    return response.data;
  },
  getStats: async (): Promise<PushStats> => {
    const response = await api.get('/push/stats');
    return response.data;
  },
};

export const notificationsAPI = {
  getNotifications: async (unreadOnly = false, skip = 0, take = 30): Promise<AppNotification[]> => {
    const response = await api.get('/notifications', { params: { unreadOnly, skip, take } });
    return response.data;
  },
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },
  markAsRead: async (id: number): Promise<void> => {
    await api.post(`/notifications/${id}/read`);
  },
  markAllAsRead: async (): Promise<void> => {
    await api.post('/notifications/read-all');
  },
  deleteNotification: async (id: number): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },
};

export const invitationsAPI = {
  getAll: async (): Promise<Invitation[]> => {
    const response = await api.get('/invitations');
    return response.data;
  },
  create: async (data: CreateInvitationRequest): Promise<CreatedInvitation> => {
    const response = await api.post('/invitations', data);
    return response.data;
  },
  /** Több meghívó egy körben, játékosokhoz kötve. Soronként külön eredmény jön vissza. */
  createBulk: async (data: BulkInvitationRequest): Promise<BulkInvitationResult> => {
    const response = await api.post('/invitations/bulk', data);
    return response.data;
  },
  revoke: async (id: number): Promise<void> => {
    await api.delete(`/invitations/${id}`);
  },
  /** Bejelentkezés nélkül hívható - a token birtoklása a jogosultság. */
  getByToken: async (token: string): Promise<InvitationPreview> => {
    const response = await api.get(`/invitations/token/${encodeURIComponent(token)}`);
    return response.data;
  },
  accept: async (token: string, name: string, password: string): Promise<AuthResponse> => {
    const response = await api.post(`/invitations/token/${encodeURIComponent(token)}/accept`, {
      name,
      password,
    });
    return response.data;
  },
};

/** A backend RegistrationRequestStatus enum értékei a szűréshez. */
const REGISTRATION_STATUS_CODES: Record<RegistrationStatus, number> = {
  pending: 1,
  approved: 2,
  rejected: 3,
};

export const registrationsAPI = {
  /** Bejelentkezés nélkül hívható - ez az önkiszolgáló jelentkezés. */
  submit: async (data: SubmitRegistrationRequest): Promise<{ id: number; email: string; status: string }> => {
    const response = await api.post('/registrations', data);
    return response.data;
  },
  getAll: async (status?: RegistrationStatus): Promise<RegistrationRequest[]> => {
    const response = await api.get('/registrations', {
      params: status ? { status: REGISTRATION_STATUS_CODES[status] } : undefined,
    });
    return response.data;
  },
  getPendingCount: async (): Promise<number> => {
    const response = await api.get('/registrations/pending-count');
    return response.data;
  },
  approve: async (id: number, data: ApproveRegistrationRequest): Promise<RegistrationRequest> => {
    const response = await api.post(`/registrations/${id}/approve`, data);
    return response.data;
  },
  reject: async (id: number, reason?: string): Promise<RegistrationRequest> => {
    const response = await api.post(`/registrations/${id}/reject`, { reason });
    return response.data;
  },
};

export const emailLogAPI = {
  getAll: async (params: {
    search?: string;
    category?: EmailCategory;
    onlyFailed?: boolean;
    skip?: number;
    take?: number;
  } = {}): Promise<EmailLogPage> => {
    const response = await api.get('/email-log', { params });
    return response.data;
  },
};

export const searchAPI = {
  search: async (query: string): Promise<SearchResponse> => {
    const response = await api.get(`/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },
};

export const boardAPI = {
  getPosts: async (options: { includeExpired?: boolean; limit?: number; eventId?: number } = {}): Promise<BoardPost[]> => {
    const response = await api.get('/board', { params: options });
    return response.data;
  },

  getPermissions: async (): Promise<BoardPermissions> => {
    const response = await api.get('/board/permissions');
    return response.data;
  },

  getPost: async (id: number): Promise<BoardPost> => {
    const response = await api.get(`/board/${id}`);
    return response.data;
  },

  create: async (data: CreateBoardPostRequest): Promise<BoardPost> => {
    const response = await api.post('/board', data);
    return response.data;
  },

  update: async (id: number, data: UpdateBoardPostRequest): Promise<BoardPost> => {
    const response = await api.put(`/board/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/board/${id}`);
  },

  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/board/unread-count');
    return response.data.count;
  },

  markRead: async (postIds: number[]): Promise<void> => {
    if (postIds.length === 0) return;
    await api.post('/board/mark-read', postIds);
  },

  getReaders: async (id: number): Promise<BoardPostReader[]> => {
    const response = await api.get(`/board/${id}/readers`);
    return response.data;
  },

  getComments: async (id: number): Promise<BoardPostComment[]> => {
    const response = await api.get(`/board/${id}/comments`);
    return response.data;
  },

  addComment: async (id: number, content: string): Promise<BoardPostComment> => {
    const response = await api.post(`/board/${id}/comments`, { content });
    return response.data;
  },

  updateComment: async (commentId: number, content: string): Promise<BoardPostComment> => {
    const response = await api.put(`/board/comments/${commentId}`, { content });
    return response.data;
  },

  deleteComment: async (commentId: number): Promise<void> => {
    await api.delete(`/board/comments/${commentId}`);
  },
};

/** Sportorvosi dátum frissítése az IFA-ból, a kijelölt játékosokra. */
export type SportDoctorSyncOutcome =
  | 'Updated'
  | 'Unchanged'
  | 'NoIfaCode'
  | 'NotFoundInIfa'
  | 'NoDateInIfa';

export interface SportDoctorSyncPlayerResult {
  playerId: number;
  playerName: string;
  outcome: number;
  previousValidity?: string | null;
  ifaValidity?: string | null;
}

export interface SportDoctorSyncResult {
  success: boolean;
  error?: string | null;
  updatedCount: number;
  players: SportDoctorSyncPlayerResult[];
}

export const profileMlszAPI = {
  save: async (email: string, password: string): Promise<void> => {
    await api.put('/profile/mlsz-credentials', { email, password });
  },

  clear: async (): Promise<void> => {
    await api.delete('/profile/mlsz-credentials');
  },
};

export const bookingsAPI = {
  canCreate: async (): Promise<boolean> => {
    const response = await api.get('/bookings/can-create');
    return response.data.allowed;
  },

  getMine: async (): Promise<Booking[]> => {
    const response = await api.get('/bookings/mine');
    return response.data;
  },

  getPending: async (): Promise<Booking[]> => {
    const response = await api.get('/bookings/pending');
    return response.data;
  },

  create: async (data: CreateBookingRequest): Promise<Booking> => {
    const response = await api.post('/bookings', data);
    return response.data;
  },

  update: async (id: number, data: CreateBookingRequest): Promise<Booking> => {
    const response = await api.put(`/bookings/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/bookings/${id}`);
  },

  approve: async (id: number): Promise<Booking> => {
    const response = await api.post(`/bookings/${id}/approve`);
    return response.data;
  },

  reject: async (id: number, reason?: string): Promise<Booking> => {
    const response = await api.post(`/bookings/${id}/reject`, { reason });
    return response.data;
  },
};

// AI funkciók (OpenRouter). Az AI válaszai lassúak lehetnek, ezért hosszabb időkorlát.
const AI_TIMEOUT = 120000;

export const aiAPI = {
  getStatus: async (): Promise<AiStatus> => {
    const response = await api.get('/ai/status');
    return response.data;
  },
  getSettings: async (): Promise<AiSettings> => {
    const response = await api.get('/ai/settings');
    return response.data;
  },
  updateSettings: async (data: UpdateAiSettingsRequest): Promise<AiSettings> => {
    const response = await api.put('/ai/settings', data);
    return response.data;
  },
  getModels: async (): Promise<AiModelOption[]> => {
    const response = await api.get('/ai/models');
    return response.data;
  },
  test: async (): Promise<AiTestResult> => {
    const response = await api.post('/ai/test', null, { timeout: AI_TIMEOUT });
    return response.data;
  },
  trainingPlan: async (data: AiTrainingPlanRequest): Promise<AiTrainingPlanResult> => {
    const response = await api.post('/ai/training-plan', data, { timeout: AI_TIMEOUT });
    return response.data;
  },
  diagram: async (description: string, title?: string): Promise<string> => {
    const response = await api.post('/ai/diagram', { description, title }, { timeout: AI_TIMEOUT });
    return response.data.diagram;
  },
  announcement: async (data: AiAnnouncementRequest): Promise<AiTextResult> => {
    const response = await api.post('/ai/announcement', data, { timeout: AI_TIMEOUT });
    return response.data;
  },
  matchSummary: async (eventId: number, data: AiMatchSummaryRequest): Promise<AiTextResult> => {
    const response = await api.post(`/ai/match-summary/${eventId}`, data, { timeout: AI_TIMEOUT });
    return response.data;
  },
  coachSummary: async (data: AiCoachSummaryRequest): Promise<AiTextResult> => {
    const response = await api.post('/ai/coach-summary', data, { timeout: AI_TIMEOUT });
    return response.data;
  },
  playerEvaluation: async (playerId: number, data: AiPlayerEvaluationRequest): Promise<AiTextResult> => {
    const response = await api.post(`/ai/player-evaluation/${playerId}`, data, { timeout: AI_TIMEOUT });
    return response.data;
  },
  ask: async (question: string): Promise<AiAskResult> => {
    const response = await api.post('/ai/ask', { question }, { timeout: AI_TIMEOUT });
    return response.data;
  },
  extractPlayer: async (file: File | null, text: string): Promise<AiExtractedPlayer> => {
    const formData = new FormData();
    if (file) formData.append('file', file);
    if (text.trim()) formData.append('text', text);
    const response = await api.post('/ai/extract-player', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: AI_TIMEOUT,
    });
    return response.data;
  },
};

export default api;
